/**
 * Every user-facing string in the app (NFR-12).
 *
 * The UI is French-only, but nothing should hardcode a string in a component. That
 * indirection costs nothing now and makes a second language a data change rather than a
 * refactor. It also keeps wording consistent — one place to settle "recette" vs "fiche".
 *
 * If we ever add a language, this object becomes the `fr` entry of a real i18n library and
 * the component call sites do not change shape.
 */
export const fr = {
  app: {
    name: 'Recettes',
    tagline: "Le carnet de recettes d'Emma et Quentin",
  },
  common: {
    loading: 'Chargement…',
    error: "Une erreur s'est produite.",
    retry: 'Réessayer',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    cancel: 'Annuler',
    close: 'Fermer',
    delete: 'Supprimer',
    edit: 'Modifier',
    add: 'Ajouter',
    optional: 'facultatif',
    remove: 'Retirer',
    reorder: 'Réordonner (glisser, ou flèches au clavier)',
    noSuggestion: 'Aucune suggestion.',
    createNamed: (name: string) => `Créer « ${name} »`,
    /**
     * Durations as a French recipe writes them: `45 min`, `3 h`, `3 h 30`.
     *
     * Under an hour nothing changes. Above it, "210 min" is a number you have to divide
     * before it means anything — and on the card, where the unit is a clock glyph rather
     * than a word, it also invites being read as 2 h 10.
     *
     * The gaps are non-breaking spaces so `3 h 30` never wraps across a line. That is the
     * character NFR-36 banned from the *editor*, because no keyboard types it back into an
     * input; nothing here is ever typed back, so it is the right character to use.
     */
    duration: (minutes: number) => {
      // Written as `\u00a0` escapes, never as typed characters: a literal U+00A0 is
      // invisible in a diff and indistinguishable from the space next to it.
      if (minutes < 60) return `${minutes}\u00a0min`
      const hours = Math.floor(minutes / 60)
      const rest = minutes % 60
      // `3 h 05`, not `3 h 5` — the padded remainder is how both a clock and a French
      // recipe write it.
      return rest === 0
        ? `${hours}\u00a0h`
        : `${hours}\u00a0h\u00a0${String(rest).padStart(2, '0')}`
    },
    /** The same duration spoken, for the card's facts — which have no visible name. */
    durationSpoken: (minutes: number) => {
      const hours = Math.floor(minutes / 60)
      const rest = minutes % 60
      const parts: string[] = []
      if (hours > 0) parts.push(hours > 1 ? `${hours} heures` : `${hours} heure`)
      if (rest > 0 || hours === 0) parts.push(rest > 1 ? `${rest} minutes` : `${rest} minute`)
      return parts.join(' ')
    },
  },
  nav: {
    recipes: 'Recettes',
    newRecipe: 'Nouvelle recette',
    admin: 'Administration',
    logout: 'Se déconnecter',
  },
  auth: {
    emailLabel: 'Adresse e-mail',
    passwordLabel: 'Mot de passe',
    displayNameLabel: 'Nom affiché',
    displayNameHint: 'Facultatif — utilisé pour "Ajoutée par" sur les recettes.',
    inviteCodeLabel: "Code d'invitation",
    login: {
      heading: 'Se connecter',
      submit: 'Se connecter',
      submitting: 'Connexion…',
      noAccount: "Vous avez un code d'invitation ?",
      registerLink: "S'inscrire",
      genericError: 'Adresse e-mail ou mot de passe incorrect.',
    },
    register: {
      heading: 'Créer un compte',
      submit: "S'inscrire",
      submitting: 'Création du compte…',
      hasAccount: 'Déjà un compte ?',
      loginLink: 'Se connecter',
    },
  },
  recipeList: {
    title: 'Recettes',
    searchPlaceholder: 'Chercher une recette ou un ingrédient…',
    empty: 'Aucune recette pour le moment.',
    emptyFiltered: 'Aucune recette ne correspond à cette recherche.',
    clearFilters: 'Réinitialiser',
    // The card's facts are icon + value, so these are what a screen reader reads and what a
    // sighted reader never sees. Spelled out in full for that reason — there is no width to
    // save in a label. The time's spoken form is `common.durationSpoken`.
    cardServings: (n: number) => (n > 1 ? `${n} portions` : `${n} portion`),
    cardComments: (n: number) => (n > 1 ? `${n} commentaires` : `${n} commentaire`),
  },
  recipeDetail: {
    back: 'Retour aux recettes',
    servings: 'Portions',
    prepTime: 'Préparation',
    cookTime: 'Cuisson',
    totalTime: 'Total',
    source: 'Source',
    notes: 'Notes',
    ingredients: 'Ingrédients',
    steps: 'Étapes',
    addedBy: (name: string) => `Ajoutée par ${name}`,
    deleteConfirm: {
      title: 'Supprimer cette recette ?',
      description: 'Elle peut être restaurée depuis l’administration si besoin.',
      confirm: 'Supprimer',
    },
    notFound: 'Cette recette est introuvable.',
  },
  comments: {
    heading: 'Commentaires',
    // F-16: the invitation matters more than the label. An empty thread on someone else's
    // recipe is the one place the app says what you *can* do with it.
    empty: 'Personne n’a encore commenté. Notez ce que vous avez changé, ça resservira.',
    placeholder: 'Votre commentaire…',
    submit: 'Publier',
    submitting: 'Publication…',
    failed: 'Le commentaire n’a pas pu être publié.',
    // The author's account is gone; the note stays (SET_NULL). Never a placeholder that
    // looks like an address (NFR-39).
    formerMember: 'Ancien membre',
    delete: 'Supprimer le commentaire',
    deleteConfirm: {
      title: 'Supprimer ce commentaire ?',
      // Says the quiet part out loud: unlike a recipe, this one does not come back (F-18).
      description: 'Contrairement à une recette, un commentaire supprimé n’est pas récupérable.',
      confirm: 'Supprimer',
    },
  },
  ownership: {
    // F-7. Phrased as whose it is rather than as a refusal: nothing was done wrong, the
    // recipe simply belongs to someone else.
    readOnly: (name: string) => `Cette recette est celle de ${name}, vous pouvez la lire et la commenter.`,
    readOnlyAnonymous: 'Cette recette est celle de quelqu’un d’autre.',
    cannotEdit: 'Seule la personne qui a créé cette recette peut la modifier.',
  },
  recipeEdit: {
    newHeading: 'Nouvelle recette',
    editHeading: 'Modifier la recette',
    titleLabel: 'Titre',
    descriptionLabel: 'Description',
    servingsLabel: 'Portions',
    prepTimeLabel: 'Préparation (min)',
    cookTimeLabel: 'Cuisson (min)',
    sourceLabel: 'Source',
    notesLabel: 'Notes',
    tagsLabel: 'Étiquettes',
    tagsPlaceholder: 'Ajouter une étiquette…',
    ingredientsHeading: 'Ingrédients',
    addIngredient: 'Ajouter un ingrédient',
    ingredientNamePlaceholder: 'Nom (ex. farine)',
    ingredientQuantityPlaceholder: 'Qté',
    ingredientNotePlaceholder: 'Note (facultatif)',
    ingredientUnitPlaceholder: 'Unité',
    noUnit: 'Aucune',
    // The per-row overflow menu (F-17). `named` when the row has a name to point at,
    // `unnamed` while it is still blank — "Options : ligne 3" beats an unlabelled button.
    ingredientOptions: {
      named: (name: string) => `Options : ${name}`,
      unnamed: (position: number) => `Options : ligne ${position}`,
      noteLabel: 'Note',
      optionalLabel: 'Ingrédient facultatif',
      remove: 'Retirer l’ingrédient',
    },
    stepsHeading: 'Étapes',
    addStep: 'Ajouter une étape',
    stepPlaceholder: (n: number) => `Étape ${n}…`,
    photo: {
      label: 'Photo',
      // No "prendre une photo" / "choisir dans la galerie" split: the file input carries no
      // `capture` attribute, so the phone offers both in its own sheet and names them better
      // than we would.
      choose: 'Ajouter une photo',
      replace: 'Remplacer la photo',
      remove: 'Retirer la photo',
      tooLarge: (mb: number) => `L’image dépasse ${mb} Mo.`,
      notAnImage: 'Ce fichier n’est pas une image.',
      uploading: 'Envoi de la photo…',
      // The recipe saved and the photo did not. Says so plainly, because the alternative is
      // someone re-submitting a form that already succeeded (F-20).
      failedAfterSave:
        'La recette est enregistrée, mais la photo n’a pas pu être envoyée. Vous pouvez réessayer en la modifiant.',
    },
    // F-15 marks the required fields where they are, in one continuous form. It used to
    // split them into two blocks; the markers were the part that worked.
    requiredMark: 'obligatoire',
    optionalSuffix: '(facultatif)',
    validation: {
      summary: 'Il manque quelques informations.',
      titleRequired: 'Le titre est obligatoire.',
      servingsRequired: 'Indiquez le nombre de portions.',
      prepTimeRequired: 'Indiquez le temps de préparation.',
      cookTimeRequired: 'Indiquez le temps de cuisson (0 si aucune).',
      ingredientsRequired: 'Il faut au moins un ingrédient.',
      stepsRequired: 'Il faut au moins une étape.',
      ingredientNameRequired: 'Chaque ingrédient a besoin d’un nom.',
      stepTextRequired: 'Chaque étape a besoin d’un texte.',
      saveFailed: 'L’enregistrement a échoué.',
    },
  },
} as const
